import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  School,
  BookOpen,
  Shield,
  BarChart3,
  CreditCard,
  Bell,
  Flag,
} from 'lucide-react';
import { TopNav, Sidebar, MobileNav } from 'components/navigation/TopNav';
import { PageTransition } from 'components/ui/PageTransition';
import { ROUTES } from 'constants/routes';
import { usePermission } from 'shared/permissions/usePermission';
import type { Permission } from 'types';

const allAdminLinks: {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  permission?: Permission;
}[] = [
  { label: 'Overview', to: ROUTES.ADMIN, icon: LayoutDashboard },
  { label: 'Users', to: ROUTES.ADMIN_USERS, icon: Users },
  { label: 'Teachers', to: ROUTES.ADMIN_TEACHERS, icon: GraduationCap },
  { label: 'Schools', to: ROUTES.ADMIN_SCHOOLS, icon: School },
  { label: 'Batches', to: ROUTES.ADMIN_BATCHES, icon: BookOpen },
  {
    label: 'Course Builder',
    to: ROUTES.ADMIN_COURSE_BUILDER,
    icon: BookOpen,
    permission: 'course:create' as const,
  },
  {
    label: 'Permissions',
    to: ROUTES.ADMIN_PERMISSIONS,
    icon: Shield,
    permission: 'admin:manage' as const,
  },
  { label: 'Analytics', to: ROUTES.ADMIN_ANALYTICS, icon: BarChart3 },
  {
    label: 'Payments',
    to: ROUTES.ADMIN_PAYMENTS,
    icon: CreditCard,
    permission: 'payment:view' as const,
  },
  { label: 'Notifications', to: ROUTES.ADMIN_NOTIFICATIONS, icon: Bell },
  { label: 'Moderation', to: ROUTES.ADMIN_MODERATION, icon: Flag },
];

export function AdminLayout({ children }: { children?: ReactNode }) {
  const canViewPayments = usePermission('payment:view');
  const canManageAdmin = usePermission('admin:manage');
  const canCreateCourse = usePermission('course:create');

  const adminLinks = allAdminLinks.filter((link) => {
    if (!link.permission) return true;
    if (link.permission === 'payment:view') return canViewPayments;
    if (link.permission === 'admin:manage') return canManageAdmin;
    if (link.permission === 'course:create') return canCreateCourse;
    return true;
  });
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <div className="flex flex-1">
        <Sidebar links={adminLinks} title="Administration" />
        <MobileNav links={adminLinks} />
        <main className="flex-1 p-4 lg:p-8 overflow-auto bg-surface-secondary/30">
          <div className="mx-auto max-w-7xl">
            <PageTransition>
              {children ?? <Outlet />}
            </PageTransition>
          </div>
        </main>
      </div>
    </div>
  );
}
